/**
 * TA capacity planning — how much of the hiring demand the team can cover.
 *
 * ⚠️ THE DEMAND SIDE DOES NOT COME FROM THE SHEET. The old model built its target out of
 * `planned_total` plus a modelled backfill, both read from named ranges somebody maintained by
 * hand. Demand is the Hiring Database's own answer, via the Tracking payload — the same figure the
 * Hiring demand board shows, so the two tabs cannot disagree about how many people we are hiring.
 *
 * ⚠️ BACKFILLS ARE NOT MODELLED INTO THE DEMAND. In the sheet they were an ESTIMATE (headcount ×
 * attrition) because the headcount plan listed only new roles. In the HDB a backfill is a ROW, with
 * a reason and a date, so adding a modelled figure on top would count the raised ones twice. What
 * the model can still say is how many are expected but NOT YET RAISED: an explicit lever,
 * defaulting to none, added to demand rather than hidden inside it.
 *
 * ⚠️ THE TEAM SIDE IS A ROSTER OF PEOPLE, not two numbers per cluster. `clusterTeamsFrom` derives
 * FTE from contracted hours, so "Anna goes to 20 h" is an edit to Anna rather than to a cluster
 * total nobody can trace back to a person. See `types/taTeam.ts`.
 *
 * ⚠️ HIRES PER MONTH IS THE ASSUMPTION, and there is no efficiency multiplier beside it. There used
 * to be both: a per-cluster HPM and a global "efficiency gain" percentage that scaled it. Two
 * controls for one quantity, and the honest question — what do we think a recruiter delivers a
 * month — was the one you could not answer directly. A team getting faster is a higher HPM.
 */
import {
  CLUSTER_KEYS,
  UNASSIGNED,
  isIntern,
  isPlanned,
  type ClusterKey,
  type TeamMember,
} from '../types/taTeam';

export { CLUSTER_KEYS };
export type { ClusterKey };

/** One cluster's recruiting capacity, derived from the roster. */
export interface ClusterTeam {
  key: ClusterKey;
  /** Recruiter FTE the cluster has, counting only hours planned as hiring capacity. */
  fte: number;
  /** Intern / working-student FTE it has. */
  interns: number;
}

/**
 * Everything the model assumes, in one place.
 *
 * All four are the TEAM'S OWN ESTIMATES rather than measurements, and the tab says so. Two weeks of
 * activity-tagged time tracking would settle the first two — the same open question Role
 * Prioritisation records about its effort model.
 */
export interface CapacityAssumptions {
  /** What one FTE means, in contracted hours a week. Everything else divides by it. */
  fullWeekHours: number;
  /** Hires one recruiter FTE delivers in a month, per cluster. THE model's central number. */
  hpm: Record<ClusterKey, number>;
  /** What one intern FTE delivers against one recruiter FTE. */
  internProductivity: number;
  /**
   * Backfills expected this trimester that NOBODY HAS RAISED YET.
   *
   * Zero by default and deliberately so: the HDB already holds every backfill that exists, and a
   * non-zero default would quietly re-introduce the double count this model was rebuilt to remove.
   */
  unraisedBackfills: number;
}

/** Whole trimester, or only what is left of it. */
export type Horizon = 'trimester' | 'remaining';

export interface ClusterCoverage {
  key: ClusterKey;
  demand: number;
  capacity: number;
  gap: number;
  coverage: number;
  fte: number;
  interns: number;
}

export interface CapacityResult {
  months: number;
  demand: number;
  capacity: number;
  /** What the team can actually deliver, summed per cluster — never pooled. */
  covered: number;
  shortfall: number;
  strandedCapacity: number;
  /** `covered ÷ demand`. What share of the plan the team can actually deliver. */
  coverage: number;
  clusters: ClusterCoverage[];
}

/**
 * The seeded assumptions.
 *
 * HPM per cluster came from the TA Cockpit sheet's `ref_hpm_*` and is seeded here so the tab works
 * when the sheet does not. Every value is editable on screen.
 */
export const DEFAULT_ASSUMPTIONS: CapacityAssumptions = {
  fullWeekHours: 40,
  hpm: { Growth: 2, Operations: 2.5, 'Core Functions': 1.8 },
  internProductivity: 0.5,
  unraisedBackfills: 0,
};

/**
 * The roster, as capacity per cluster.
 *
 * Someone not planned as hiring capacity contributes nothing — their hours are on the team and in
 * no figure. Someone `Unassigned` contributes to no cluster, which is truthful rather than tidy:
 * their hours belong to no cluster's load, and spreading them would invent capacity somewhere.
 */
export function clusterTeamsFrom(members: TeamMember[], fullWeekHours: number): ClusterTeam[] {
  const week = fullWeekHours > 0 ? fullWeekHours : 40;
  return CLUSTER_KEYS.map((key) => {
    const mine = members.filter((m) => m.cluster === key && isPlanned(m) && m.hours > 0);
    const fteOf = (list: TeamMember[]) => list.reduce((s, m) => s + m.hours, 0) / week;
    return {
      key,
      fte: fteOf(mine.filter((m) => !isIntern(m))),
      interns: fteOf(mine.filter(isIntern)),
    };
  });
}

/** People on the roster carrying no cluster — reported, never distributed. */
export function unassignedMembers(members: TeamMember[]): TeamMember[] {
  return members.filter((m) => m.cluster === UNASSIGNED && m.hours > 0 && isPlanned(m));
}

/**
 * Months of runway in the horizon.
 *
 * `remaining` is what matters mid-trimester: measuring four months of capacity against the work
 * still to do would claim the team can deliver in November the hires it had September, October and
 * the rest of the trimester to make. Never below zero, never above the trimester's own length.
 */
export function monthsIn(horizon: Horizon, elapsedDays: number, totalDays: number): number {
  const full = 4;
  if (horizon === 'trimester') return full;
  if (!totalDays) return full;
  const left = Math.max(0, totalDays - elapsedDays);
  return Math.min(full, (left / totalDays) * full);
}

/** One cluster's hires over `months`, at the current assumptions. */
export function clusterCapacity(
  team: ClusterTeam,
  a: CapacityAssumptions,
  months: number,
): number {
  const hpm = a.hpm[team.key] ?? 0;
  return (team.fte + team.interns * a.internProductivity) * hpm * months;
}

/**
 * The whole picture: demand per cluster against capacity per cluster.
 *
 * `demandByCluster` comes from the Tracking payload — live HDB rows attributed by `People Team`.
 * Roles no cluster could be attributed to are NOT distributed here: they arrive in
 * `unattributedDemand` and are added to the company total only, because splitting them across
 * clusters would move the figure a cluster lead is judged on.
 */
export function computeCapacity({
  members,
  assumptions,
  demandByCluster,
  unattributedDemand = 0,
  horizon,
  elapsedDays,
  totalDays,
}: {
  members: TeamMember[];
  assumptions: CapacityAssumptions;
  demandByCluster: Record<ClusterKey, number>;
  unattributedDemand?: number;
  horizon: Horizon;
  elapsedDays: number;
  totalDays: number;
}): CapacityResult {
  const months = monthsIn(horizon, elapsedDays, totalDays);
  const teams = clusterTeamsFrom(members, assumptions.fullWeekHours);

  const clusters: ClusterCoverage[] = teams.map((t) => {
    const capacity = clusterCapacity(t, assumptions, months);
    const demand = demandByCluster[t.key] ?? 0;
    return {
      key: t.key,
      demand,
      capacity,
      gap: capacity - demand,
      coverage: demand > 0 ? capacity / demand : capacity > 0 ? Infinity : 1,
      fte: t.fte,
      interns: t.interns,
    };
  });

  const capacity = clusters.reduce((s, c) => s + c.capacity, 0);

  /*
   * Demand nobody's cluster owns: roles the attribution rules could not place, plus backfills that
   * have not been raised yet. Kept apart from the clusters for opposite reasons — one has no
   * cluster recorded, the other has no leaver yet — but they share the property that matters here:
   * SPARE capacity anywhere can take them on, because they are tied to no pipeline.
   */
  const unclustered = unattributedDemand + Math.max(0, assumptions.unraisedBackfills);
  const clusterDemand = clusters.reduce((s, c) => s + c.demand, 0);
  const demand = clusterDemand + unclustered;

  /*
   * ⚠️ PER CLUSTER, AND ONLY PER CLUSTER. A cluster covers its own demand up to its own capacity
   * and not one hire more: 14 spare in one cluster and 14 short in another is a company gap of
   * zero and fourteen hires the team cannot make.
   */
  const coveredInClusters = clusters.reduce((s, c) => s + Math.min(c.demand, c.capacity), 0);
  const spare = clusters.reduce((s, c) => s + Math.max(0, c.capacity - c.demand), 0);
  const covered = coveredInClusters + Math.min(unclustered, spare);

  return {
    months,
    demand,
    capacity,
    covered,
    shortfall: Math.max(0, demand - covered),
    strandedCapacity: Math.max(0, capacity - covered),
    coverage: demand > 0 ? covered / demand : 1,
    clusters,
  };
}

/**
 * Hires per recruiter FTE per month, as it ACTUALLY ran — the measured counterpart to the `hpm`
 * assumption, shown beside it so the estimate can be checked rather than believed.
 *
 * Null rather than zero when there is nothing to divide by: a cluster with no recruiters, or a
 * trimester that has not started, has no rate — and a confident 0.0 beside an assumption of 2.5
 * reads as a team that has stopped delivering.
 */
export function actualHpm(hired: number, fte: number, monthsElapsed: number): number | null {
  if (!(fte > 0) || !(monthsElapsed > 0)) return null;
  return hired / fte / monthsElapsed;
}

/** Months of the trimester already gone, from the pace figures the Tracking payload carries. */
export function monthsElapsed(elapsedDays: number, totalDays: number): number {
  if (!(totalDays > 0)) return 0;
  return Math.max(0, Math.min(4, (elapsedDays / totalDays) * 4));
}
